/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import React from 'react';
import { format, formatDistanceToNow } from 'date-fns';
import { Tag, Tone } from '../console/ui';
import { AuditLog } from '../../types';
import { isVMManagerEnabled } from '../../config/featureFlags';

export const AUDIT_ACTIONS: Array<{ value: string; label: string }> = [
  { value: 'create', label: 'Create' },
  { value: 'update', label: 'Update' },
  { value: 'delete', label: 'Delete' },
  { value: 'start', label: 'Start' },
  { value: 'stop', label: 'Stop' },
  { value: 'restart', label: 'Restart' },
  { value: 'scale', label: 'Scale' },
  { value: 'login', label: 'Login' },
  { value: 'logout', label: 'Logout' },
  { value: 'invite', label: 'Invite' },
  { value: 'join', label: 'Join' },
  { value: 'remove', label: 'Remove' },
];

/** Resource filter options. VM is only offered when the VM manager is enabled. */
export const auditResourceOptions = (): Array<{ value: string; label: string }> => [
  { value: 'container', label: 'Pod' },
  ...(isVMManagerEnabled() ? [{ value: 'vm', label: 'VM' }] : []),
  { value: 'bucket', label: 'Bucket' },
  { value: 'nfs', label: 'NFS volume' },
  { value: 'template', label: 'Template' },
  { value: 'ssh_key', label: 'SSH key' },
  { value: 'user', label: 'User' },
  { value: 'project_member', label: 'Project member' },
];

const RESOURCE_LABELS: Record<string, string> = {
  container: 'Pod',
  vm: 'VM',
  bucket: 'Bucket',
  nfs: 'NFS volume',
  template: 'Template',
  ssh_key: 'SSH key',
  user: 'User',
  project_member: 'Project member',
};

export const resourceLabel = (type?: string) =>
  (type && RESOURCE_LABELS[type]) ||
  (type || 'Unknown')
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

export const actionTone = (action?: string): Tone => {
  switch (action) {
    case 'create':
    case 'start':
    case 'join':
      return 'ok';
    case 'delete':
    case 'remove':
      return 'err';
    case 'stop':
      return 'warn';
    case 'update':
    case 'scale':
    case 'restart':
    case 'invite':
    case 'connect':
      return 'info';
    case 'login':
      return 'accent';
    default:
      return 'off';
  }
};

interface AuditLogTableProps {
  logs: AuditLog[];
  projectNames?: Record<string, string>;
  showProject?: boolean;
  emptyMessage: React.ReactNode;
  highlight?: Set<AuditLog>;
}

const AuditLogTable: React.FC<AuditLogTableProps> = ({ logs, projectNames, showProject = true, emptyMessage, highlight }) => {
  const cols = showProject ? 6 : 5;
  return (
    <div className="ps-card overflow-x-auto">
      <table className="ps-tbl">
        <thead>
          <tr>
            <th>Time</th>
            <th>User</th>
            <th>Action</th>
            <th>Resource</th>
            {showProject && <th>Project</th>}
            <th>IP</th>
          </tr>
        </thead>
        <tbody>
          {logs.length === 0 ? (
            <tr>
              <td colSpan={cols} className="!py-10 text-center text-ps-muted !whitespace-normal">
                {emptyMessage}
              </td>
            </tr>
          ) : (
            logs.map((log, i) => {
              const d = new Date(log.created_at);
              return (
                <tr key={log.id || i} className={highlight?.has(log) ? 'bg-ps-ok-bg/40' : undefined}>
                  <td title={format(d, 'PPpp')}>
                    <div className="text-ps-text">{format(d, 'd MMM, HH:mm')}</div>
                    <div className="text-xs text-ps-subtle">{formatDistanceToNow(d, { addSuffix: true })}</div>
                  </td>
                  <td>{log.user_email || <span className="text-ps-subtle">Unknown user</span>}</td>
                  <td>
                    <Tag tone={actionTone(log.action)}>{log.action}</Tag>
                  </td>
                  <td>
                    <span className="text-ps-muted">{resourceLabel(log.resource_type)}</span>
                    {log.resource_name && (
                      <span className="ml-1.5 text-ps-text inline-block max-w-[220px] truncate align-bottom" title={log.resource_name}>
                        {log.resource_name}
                      </span>
                    )}
                  </td>
                  {showProject && (
                    <td className="text-ps-muted">{projectNames?.[log.project_id] || <span className="text-ps-subtle">-</span>}</td>
                  )}
                  <td className="font-mono text-xs text-ps-muted">{log.ip_address || '-'}</td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
};

export default AuditLogTable;
